/**
 * /settings/api-key — Clé API MCP
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PublicHeader, Breadcrumb } from '@/components/jurisprudence';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { SettingsNav } from './SettingsProfile';
import { getDateLocale } from '@/i18n';

const AUTH_BASE = '/api/auth';

async function apiFetch(path: string, token: string, options: RequestInit = {}) {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

interface MCPKeyData {
  has_key:     boolean;
  key?:        string;
  key_masked?: string;
  plan?:       string;
  created_at?: string;
  last_used?:  string;
}

export default function SettingsApiKey() {
  const { t } = useTranslation();
  const navigate                    = useNavigate();
  const { user, loading, getToken } = usePublicAuth();

  const [mcpKey,     setMcpKey]     = useState<MCPKeyData | null>(null);
  const [loadingKey, setLoadingKey] = useState(true);
  const [newKey,     setNewKey]     = useState<string | null>(null);
  const [copied,     setCopied]     = useState<string | null>(null); // 'key' | 'url'
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/login?redirect=/settings/api-key', { replace: true });
  }, [user, loading, navigate]);

  const loadKey = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingKey(true);
    try {
      const data = await apiFetch('/mcp-keys', token);
      setMcpKey(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingKey(false);
    }
  }, [getToken]);

  useEffect(() => { if (user) loadKey(); }, [user, loadKey]);

  const handleGenerate = async () => {
    setError(''); setSuccess('');
    const token = await getToken();
    if (!token) return;
    setLoadingKey(true);
    try {
      const data = await apiFetch('/mcp-keys/generate', token, { method: 'POST' });
      setNewKey(data.key);
      setSuccess(t('settings.api_key.generated'));
      await loadKey();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingKey(false);
    }
  };

  const handleRevoke = async () => {
    setError(''); setSuccess('');
    const token = await getToken();
    if (!token) return;
    setLoadingKey(true);
    setConfirming(false);
    try {
      await apiFetch('/mcp-keys', token, { method: 'DELETE' });
      setMcpKey({ has_key: false });
      setNewKey(null);
      setSuccess(t('settings.api_key.revoked'));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingKey(false);
    }
  };

  const handleCopy = (text: string, which: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const mcpUrl = newKey
    ? `https://mcp.smartlawyer.ai/mcp?api_key=${newKey}`
    : mcpKey?.key
    ? `https://mcp.smartlawyer.ai/mcp?api_key=${mcpKey.key}`
    : null;

  if (loading) return <Spinner />;
  if (!user)   return null;

  return (
    <>
      <Helmet><title>{t('settings.api_key.page_title')}</title></Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb items={[{ label: t('settings.breadcrumb_home'), to: '/' }, { label: t('settings.my_account') }]} />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-10">
          <SettingsNav active="api-key" />

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400">
              {success}
            </div>
          )}

          <section className="rounded-xl border bg-card p-6 mt-6">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-base font-semibold">{t('settings.api_key.title')}</h2>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                mcpKey?.plan === 'paid'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {mcpKey?.plan === 'paid' ? t('settings.api_key.subscriber') : t('common.free')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {t('settings.api_key.description')}
            </p>

            {loadingKey ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Clé visible une seule fois */}
                {newKey && (
                  <div className="mb-5 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                        {t('settings.api_key.copy_now')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono bg-background rounded px-3 py-2 border break-all select-all">
                        {newKey}
                      </code>
                      <button
                        onClick={() => handleCopy(newKey, 'key')}
                        className="flex-shrink-0 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                      >
                        {copied === 'key' ? t('settings.api_key.copied') : t('common.copy')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Clé masquée existante */}
                {!newKey && mcpKey?.has_key && mcpKey.key_masked && (
                  <div className="mb-5 p-4 rounded-lg bg-muted/50 border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('settings.api_key.active_key')}</p>
                        <code className="text-sm font-mono">{mcpKey.key_masked}</code>
                      </div>
                      {mcpKey.last_used && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{t('settings.api_key.last_used')}</p>
                          <p className="text-xs font-medium mt-0.5">
                            {new Date(mcpKey.last_used).toLocaleDateString(getDateLocale())}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* URL de connexion MCP */}
                {mcpUrl && (
                  <div className="mb-6">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {t('settings.api_key.url_label')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono bg-muted rounded px-3 py-2.5 break-all">
                        {mcpUrl}
                      </code>
                      <button
                        onClick={() => handleCopy(mcpUrl, 'url')}
                        className="flex-shrink-0 px-3 py-2 rounded-lg border text-xs font-medium hover:bg-accent transition-colors"
                      >
                        {copied === 'url' ? '✓' : t('common.copy')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="mb-6 p-4 rounded-lg bg-muted/30 border">
                  <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-3">
                    {t('settings.api_key.how_to')}
                  </p>
                  <ol className="space-y-2">
                    {[
                      t('settings.api_key.steps.generate'),
                      t('settings.api_key.steps.config'),
                      t('settings.api_key.steps.paste'),
                      t('settings.api_key.steps.restart'),
                    ].map((step, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                        <span className="font-mono text-xs bg-background border rounded px-1.5 py-0.5 flex-shrink-0 h-fit mt-0.5">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                  <p className="text-xs mt-3 pt-3 border-t">
                    {t('settings.api_key.documentation_full')}{' '}
                    <a href="https://mcp.smartlawyer.ai" target="_blank" rel="noopener noreferrer"
                      className="text-primary hover:underline">
                      mcp.smartlawyer.ai
                    </a>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={handleGenerate}
                    disabled={loadingKey}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {loadingKey
                      ? t('settings.api_key.loading')
                      : mcpKey?.has_key
                      ? t('settings.api_key.regenerate')
                      : t('settings.api_key.generate')}
                  </button>

                  {mcpKey?.has_key && !confirming && (
                    <button
                      onClick={() => setConfirming(true)}
                      className="px-4 py-2 rounded-lg border text-sm text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                    >
                      {t('settings.api_key.revoke')}
                    </button>
                  )}

                  {confirming && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('settings.api_key.confirm_short')}</span>
                      <button
                        onClick={handleRevoke}
                        className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
                      >
                        {t('settings.api_key.yes_revoke')}
                      </button>
                      <button
                        onClick={() => setConfirming(false)}
                        className="px-3 py-1.5 rounded-lg border text-xs hover:bg-accent transition-colors"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  )}
                </div>

                {mcpKey?.has_key && (
                  <p className="text-xs text-muted-foreground mt-3">
                    {t('settings.api_key.regenerate_warning')}
                  </p>
                )}
              </>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}